import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject, Subscription, interval } from "rxjs";

//above this many jobs, generation is queued and run in batches instead of the regular flow
export const JO_GENERATION_QUEUE_THRESHOLD = 25;

export interface JoGenerationQueueState {
  trackingId: string;
  createdCount: number;
  totalCount: number;
}

@Injectable({
  providedIn: "root",
})
export class JoGenerationQueueService {
  //null means no queued generation is running
  private stateSubject = new BehaviorSubject<JoGenerationQueueState | null>(null);
  state$ = this.stateSubject.asObservable();

  //emits the final state once a queued run finishes
  private completedSubject = new Subject<JoGenerationQueueState>();
  completed$ = this.completedSubject.asObservable();

  private pollSub: Subscription | null = null;

  get isInProgress(): boolean {
    return !!this.stateSubject.value;
  }

  startQueue(trackingId: string, totalCount: number) {
    this.stateSubject.next({ trackingId, createdCount: 0, totalCount });
    this.startPolling();
  }

  // TODO: replace the mock below with a real status call using trackingId once the API is available.
  // Should also be called on planner load to pick up a run that is already in progress.
  private startPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(2000).subscribe(() => {
      const state = this.stateSubject.value;
      if (!state) {
        this.stopPolling();
        return;
      }
      const createdCount = Math.min(state.createdCount + 25, state.totalCount);
      const updated = { ...state, createdCount };
      if (createdCount >= state.totalCount) {
        this.stopPolling();
        this.stateSubject.next(null);
        this.completedSubject.next(updated);
      } else {
        this.stateSubject.next(updated);
      }
    });
  }

  private stopPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }
}
